import { createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { useWire } from "~/wire";
import { TbX } from "solid-icons/tb";
import { StButton } from "@storytell/ui/buttons";
import { usePromptBarContext } from "../../PromptBarContext";
import { ScopeManagerPaneAssetsTable, useScopeManagerPaneAssetsData } from "./ScopeManagerPaneAssetsTable";
import { ScopeManagerPaneConceptsTable, useScopeManagerPaneConceptsData } from "./ScopeManagerPaneConceptsTable";

export const ScopeManagerPane = (props: { onClose?: () => void }) => {
  let ref!: HTMLDivElement;
  let inputRef!: HTMLInputElement;
  onMount(() => {
    inputRef.focus();
  });

  const [style, setStyle] = createSignal<{
    "max-width": string;
    "max-height": string;
  }>({
    "max-width": "100%",
    "max-height": "100%",
  });

  onMount(() => {
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const wh = window.innerHeight;
        const ww = window.innerWidth;
        const { top, left } = ref.getBoundingClientRect();

        const maxWidth = ww - left;
        const maxHeight = wh - top;

        setStyle({
          "max-height": `${maxHeight - 10}px`,
          "max-width": `${maxWidth - 10}px`,
        });
      });
    });

    resizeObserver.observe(ref);
    resizeObserver.observe(document.body);
    onCleanup(() => {
      resizeObserver.disconnect();
    });
  });

  // Search state
  const [search, setSearch] = createSignal("");
  const { assets } = useScopeManagerPaneAssetsData(search);
  const { concepts } = useScopeManagerPaneConceptsData(search);

  return (
    <div class="w-full max-w-4xl p-2 flex flex-col" ref={ref} style={style()}>
      <div class="flex items-center justify-between mb-3 gap-2">
        <h3 class="text-primary-text font-medium text-sm">Add to scope</h3>
        <StButton
          icon={TbX}
          aria-label="Close"
          class="ml-2 shrink-0"
          noColor
          noBorder
          onClick={() => props.onClose?.()}
        />
      </div>

      <div class="mb-3 flex items-center gap-2 shrink-0">
        <input
          type="text"
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search all my Knowledge..."
          class="w-full px-3 py-2 border border-primary-border rounded bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          aria-label="Search assets"
          ref={inputRef}
        />
      </div>

      <ScopeManagerPaneAssetsTable assets={assets()} />
      <ScopeManagerPaneConceptsTable concepts={concepts()} />

      <p class="text-secondary-text text-xs mb-2">
        Storytell will use all your Knowledge to answer if you don't specify a scope.{" "}
        <a href="https://go.storytell.ai/learn-scope" target="_blank" rel="noreferrer" class="text-brand underline">
          Learn more
        </a>
      </p>
    </div>
  );
};
